import { Field, FieldDescription, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DatePicker } from '@/components/reui/date-picker';

export function TextField({
  id,
  label,
  error,
  description,
  ...props
}: React.ComponentProps<typeof Input> & {
  id: string;
  label: string;
  error?: string | undefined;
  description?: string | undefined;
}) {
  return (
    <Field data-invalid={!!error} data-disabled={props.disabled}>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <Input
        id={id}
        name={id}
        aria-invalid={!!error}
        aria-describedby={
          [description ? `${id}-description` : '', error ? `${id}-error` : '']
            .filter(Boolean)
            .join(' ') || undefined
        }
        {...props}
      />
      {description && <FieldDescription id={`${id}-description`}>{description}</FieldDescription>}
      {error && <FieldError id={`${id}-error`}>{error}</FieldError>}
    </Field>
  );
}

export function TextAreaField({
  id,
  label,
  error,
  description,
  ...props
}: React.ComponentProps<typeof Textarea> & {
  id: string;
  label: string;
  error?: string | undefined;
  description?: string | undefined;
}) {
  return (
    <Field data-invalid={!!error} data-disabled={props.disabled}>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <Textarea
        id={id}
        name={id}
        aria-invalid={!!error}
        aria-describedby={
          [description ? `${id}-description` : '', error ? `${id}-error` : '']
            .filter(Boolean)
            .join(' ') || undefined
        }
        {...props}
      />
      {description && <FieldDescription id={`${id}-description`}>{description}</FieldDescription>}
      {error && <FieldError id={`${id}-error`}>{error}</FieldError>}
    </Field>
  );
}

export function ChoiceField<T extends string>({
  id,
  label,
  value,
  options,
  onChange,
  onBlur,
  disabled = false,
  required = false,
  error,
  description,
}: {
  id: string;
  label: string;
  value: T;
  options: { value: NoInfer<T>; label: string; description?: string }[];
  onChange(value: NoInfer<T>): void;
  onBlur?: (() => void) | undefined;
  disabled?: boolean;
  required?: boolean;
  error?: string | undefined;
  description?: string | undefined;
}) {
  const selectedDescription = options.find((option) => option.value === value)?.description;
  const hasDescriptions = options.some((option) => option.description);

  return (
    <Field data-invalid={!!error} data-disabled={disabled}>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <Select
        value={value}
        onValueChange={(next) => {
          if (next !== null) onChange(next);
        }}
        items={options}
        disabled={disabled}
        required={required}
      >
        <SelectTrigger
          id={id}
          treatment="wrap"
          onBlur={onBlur}
          className="w-full"
          aria-invalid={!!error}
          aria-describedby={
            [
              selectedDescription ? `${id}-selection-description` : '',
              description ? `${id}-description` : '',
              error ? `${id}-error` : '',
            ]
              .filter(Boolean)
              .join(' ') || undefined
          }
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent alignItemWithTrigger={!hasDescriptions}>
          <SelectGroup>
            {options.map((option) => (
              <SelectItem
                key={option.value}
                value={option.value}
                className="[&>div]:min-w-0 [&>div]:shrink [&>div]:whitespace-normal"
              >
                {option.description ? (
                  <span className="flex min-w-0 flex-col gap-1 py-1">
                    <span className="font-medium">{option.label}</span>
                    <span className="text-xs text-muted-foreground">{option.description}</span>
                  </span>
                ) : (
                  option.label
                )}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
      {selectedDescription && (
        <FieldDescription id={`${id}-selection-description`}>
          {selectedDescription}
        </FieldDescription>
      )}
      {description && <FieldDescription id={`${id}-description`}>{description}</FieldDescription>}
      {error && <FieldError id={`${id}-error`}>{error}</FieldError>}
    </Field>
  );
}

export function DateField({
  id,
  label,
  value,
  onChange,
  onBlur,
  description,
  error,
  clearable = false,
  disabled = false,
  min,
  max,
}: {
  id: string;
  label: string;
  value: string;
  onChange(value: string): void;
  onBlur?: (() => void) | undefined;
  description?: string | undefined;
  error?: string | undefined;
  clearable?: boolean;
  disabled?: boolean;
  min?: string | undefined;
  max?: string | undefined;
}) {
  const describedBy = [description ? `${id}-description` : '', error ? `${id}-error` : '']
    .filter(Boolean)
    .join(' ');

  return (
    <Field data-invalid={!!error} data-disabled={disabled}>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <DatePicker
        id={id}
        label={label}
        value={value}
        onValueChange={onChange}
        onBlur={onBlur}
        size="xl"
        appearance="field"
        className="w-full min-w-0"
        invalid={!!error}
        describedBy={describedBy || undefined}
        clearable={clearable}
        disabled={disabled}
        min={min}
        max={max}
      />
      {description && <FieldDescription id={`${id}-description`}>{description}</FieldDescription>}
      {error && <FieldError id={`${id}-error`}>{error}</FieldError>}
    </Field>
  );
}
